import { Paper, Table } from "@mantine/core"
import type { UseQueryOptions } from "@tanstack/react-query"
import { useEffect } from "react"
import { AppTable, NoData, TableHeaders, TableSkeleton } from "@/components"
import { useAPIResources, useEnsureUsers } from "@/hooks"
import type { TUserSession } from "@/types"
import { type IAPIParams, UserSessionContext, usersessionAPI } from "@/utils"

interface IProps extends IAPIParams, Omit<UseQueryOptions, "queryKey" | "queryFn"> {
  headers?: React.ReactNode[]
  transform?: (data: TUserSession[]) => TUserSession[]
  children?: React.ReactNode
  setTotal?: (total: number) => void
  withProgress?: boolean
  fallback?: React.ReactNode
}

export function UserSessionsTable({ children, headers, transform, setTotal, fallback, ...params }: IProps) {
  const { isPending, data: { data, headers: dataHeaders } = {} } = useAPIResources(usersessionAPI, {
    // Query params
    refetchOnMount: true,
    refetchInterval: 60e3,
    ...params,
  })
  const total = Number(dataHeaders?.["x-total"] || 0)

  useEffect(() => {
    if (setTotal) setTotal(total)
  }, [dataHeaders, setTotal])

  useEnsureUsers(data ? data.map((us) => us.user_id).filter((uid): uid is string => !!uid) : [])

  if (isPending) return null

  if (!data?.length)
    return (
      <Paper my="xl" p="xl">
        {fallback ?? <NoData />}
      </Paper>
    )

  const usersessions = transform ? transform(data ?? []) : data

  return (
    <AppTable withRowBorders={!isPending}>
      <TableHeaders>{headers}</TableHeaders>
      <Table.Tbody>
        {isPending ? (
          <TableSkeleton colSpan={headers?.length || 1} h={20} />
        ) : (
          usersessions?.map((userses) => (
            <Table.Tr key={userses.id}>
              <UserSessionContext.Provider value={userses}>{children}</UserSessionContext.Provider>
            </Table.Tr>
          ))
        )}
      </Table.Tbody>
    </AppTable>
  )
}
